import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ChevronRight, BookOpen, Notebook, Play, Mic, Newspaper,
  MessageCircle, Download, Sparkles, Calendar, Users, Headphones,
  Feather, Video, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandHeader from "@/components/BrandHeader";
import BrandFooter from "@/components/BrandFooter";
import EmailSignup from "@/components/EmailSignup";
import BuyModal from "@/components/BuyModal";
import { track, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const CATEGORIES = {
  guides: {
    Icon: BookOpen,
    title: "Free Guides",
    tagline: "Practical, printable, and free.",
    intro:
      "Simple, one-page tools to help you take one small action today. Every guide is free, no strings — start with the flagship 7-Day Discipline Challenge.",
    items: [
      { title: "The 7-Day Discipline Challenge", body: "A single-page challenge to prove to yourself that you can keep a promise for seven days in a row.", cta: "Download PDF", href: `${API}/download-challenge`, available: true, external: true },
      { title: "Morning Reset (1-Page)", body: "A tiny morning routine to steady your mind before the day pulls you sideways.", available: false },
      { title: "Nightly Reflection Card", body: "Three questions. Two minutes. One better tomorrow.", available: false },
      { title: "Habit Tracker (Printable)", body: "A monthly grid built for real people who miss days and start again.", available: false },
    ],
  },
  journals: {
    Icon: Notebook,
    title: "Journals",
    tagline: "Discipline you can hold in your hands.",
    intro:
      "Beautifully simple discipline journals that turn small daily choices into a life you're proud of. Start with the free challenge, then grow into the 30 and 90-day editions.",
    items: [
      { title: "Free 7-Day Discipline Challenge", body: "The free PDF that starts everything — lock in focus and reclaim your edge.", cta: "Download Free", href: `${API}/download-challenge`, available: true, external: true },
      { title: "30-Day Discipline Journal — $19", body: "A month of morning prompts, evening reflection, and a habit tracker built to be forgiving. Instant PDF download.", available: false, notify: "30-day" },
      { title: "90-Day Discipline Journal — $39", body: "The long version — the one that changes who you are, not just what you do. Instant PDF download.", available: false, notify: "90-day" },
    ],
  },
  videos: {
    Icon: Play,
    title: "Videos",
    tagline: "Short lessons on habits, mindset, and health.",
    intro:
      "Bite-sized videos designed to be watched on the walk, during coffee, or before bed. Real principles, no shouting, no hype.",
    items: [],
    comingSoon: {
      badge: "Launching Q2 2026",
      hero: {
        image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1600&q=80",
        pitch: "A calm, honest video series — the opposite of your Explore feed. Two-to-four minute lessons that respect your time.",
      },
      pillars: [
        { Icon: Video, title: "5-Minute Foundations", body: "Short, focused lessons on the seven pillars of wellness. Watch one before bed." },
        { Icon: Clock, title: "The Daily Reset", body: "Sixty-second grounding practices you can do at your desk, in the car, on a walk." },
        { Icon: Sparkles, title: "Deep-Dive Series", body: "Longer, once-a-month episodes on discipline, identity, and the ordinary work of getting healthier." },
      ],
      teasers: [
        { label: "Ep 01", title: "The 1% You Can Actually Keep", body: "Why tiny, boring habits outperform every ambitious morning routine." },
        { label: "Ep 02", title: "How to Miss a Day Without Quitting", body: "The forgiveness protocol — the missing skill behind every long streak." },
        { label: "Ep 03", title: "Sleep is a Skill, Not a Gift", body: "The three levers that actually move your sleep quality, ranked by leverage." },
        { label: "Ep 04", title: "Eating Like a Person, Not a Program", body: "A quiet framework for food that survives real life, travel, and bad weeks." },
      ],
      waitlistNote: "Get notified when the first episode drops. Newsletter subscribers see it a week early.",
      source: "resources_videos",
    },
  },
  podcast: {
    Icon: Mic,
    title: "Podcast",
    tagline: "Real conversations on wellness.",
    intro:
      "A future podcast for the quiet middle — the people who don't want hype, they want to actually feel better. Long-form, un-hurried, honest.",
    items: [],
    comingSoon: {
      badge: "Launching Late 2026",
      hero: {
        image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?auto=format&fit=crop&w=1600&q=80",
        pitch: "Hour-long conversations with therapists, coaches, doctors, and ordinary people who quietly rebuilt themselves. No hustle, no supplements to sell.",
      },
      pillars: [
        { Icon: Headphones, title: "Weekly Episodes", body: "One conversation each week. Sixty to ninety minutes. On the drive, on the walk, on the dishes." },
        { Icon: Users, title: "Real Guests", body: "Practitioners and everyday people — not influencers. Depth over reach." },
        { Icon: Calendar, title: "Show Notes That Matter", body: "Every episode ships with a one-page summary, the timestamps, and one action to try this week." },
      ],
      teasers: [
        { label: "S1 · Ep 01", title: "The Quiet Case for Discipline", body: "Why discipline is the most under-rated form of self-respect — with a therapist who's watched it change lives." },
        { label: "S1 · Ep 02", title: "Sleep, Stress, and the Nervous System", body: "A pragmatic conversation with a functional medicine doctor. What to actually do at 2 a.m." },
        { label: "S1 · Ep 03", title: "The Marriage of Money and Wellness", body: "Financial stress is a health issue. A frank talk about the money habits that heal." },
        { label: "S1 · Ep 04", title: "Rebuilding After a Hard Year", body: "Two ordinary people, two very different stories of coming back. Zero performance, all truth." },
      ],
      waitlistNote: "We'll email you the moment episode one drops — plus early listener bonuses.",
      source: "resources_podcast",
    },
  },
  articles: {
    Icon: Newspaper,
    title: "Articles",
    tagline: "Written encouragement, weekly.",
    intro:
      "Short, honest essays on discipline, wellness, and the ordinary work of becoming a healthier person. New articles launching soon.",
    items: [],
    comingSoon: {
      badge: "Publishing Weekly · Q2 2026",
      hero: {
        image: "https://images.unsplash.com/photo-1519682337058-a94d519337bc?auto=format&fit=crop&w=1600&q=80",
        pitch: "A quiet, weekly essay to read with your coffee. Written for people who are tired of five-hack listicles and would rather think for eight minutes.",
      },
      pillars: [
        { Icon: Feather, title: "One Essay a Week", body: "Sent every Sunday. Long enough to matter, short enough to finish." },
        { Icon: BookOpen, title: "Seven Pillars, One Voice", body: "We rotate through the seven pillars of wellness — physical, emotional, mental, social, financial, spiritual, environmental." },
        { Icon: Sparkles, title: "One Small Action", body: "Every article ends with one specific thing to try this week. No 'thoughts and prayers' journalism." },
      ],
      teasers: [
        { label: "Essay", title: "The Cost of Living Loud", body: "On the strange price we pay for a life optimised for the algorithm." },
        { label: "Essay", title: "You Are Not Behind", body: "A gentle argument against the timeline you didn't sign up for." },
        { label: "Essay", title: "The Discipline of Rest", body: "Why the people who last are the ones who learned to stop." },
        { label: "Essay", title: "How to Care About Money Without Losing Your Soul", body: "A wellness case for boring, quiet, adult finances." },
      ],
      waitlistNote: "Subscribe now — the first essay lands directly in your inbox on launch morning.",
      source: "resources_articles",
    },
  },
  community: {
    Icon: MessageCircle,
    title: "Community",
    tagline: "A quiet, kind space to grow together.",
    intro:
      "A space for the Wishing Wellness Inner Circle — a private, invite-first community for people committed to the quiet work of getting healthier.",
    items: [],
    comingSoon: {
      badge: "Opening in 2026",
      hero: {
        image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1600&q=80",
        pitch: "The opposite of your social feed — calm, moderated, actually kind. A small group of people practising the seven pillars, together.",
      },
      pillars: [
        { Icon: MessageCircle, title: "A Kind Feed", body: "Slow, moderated conversations on wellness — free from ads, trolls, and the compare-and-despair loop." },
        { Icon: Calendar, title: "Monthly Live Gatherings", body: "Real-time sessions with the founders and guest practitioners. Q&A, journaling nights, and quiet check-ins." },
        { Icon: Users, title: "Small Accountability Circles", body: "Groups of six, matched thoughtfully, that actually show up for each other." },
      ],
      teasers: [
        { label: "Included", title: "Members-only Journals", body: "Deeper workbooks and printables that never go public." },
        { label: "Included", title: "Founding-Member Pricing", body: "The first hundred members lock in the lowest price we'll ever offer." },
        { label: "Included", title: "Direct Access", body: "A private channel to talk with the founders and shape what we build next." },
      ],
      waitlistNote: "Founding-member spots are limited. Join the waitlist to be invited first.",
      ctaLabel: "Full Inner Circle details",
      ctaLink: "/inner-circle",
      source: "resources_community",
    },
  },
};

export default function ResourceCategory() {
  const { slug } = useParams();
  const cat = CATEGORIES[slug];
  const [notifyProduct, setNotifyProduct] = useState(null);
  if (!cat) return <Navigate to="/resources" replace />;

  const { Icon, title, tagline, intro, items, comingSoon } = cat;

  const priceLabelMap = { "30-day": "$19", "90-day": "$39" };
  const titleMap = { "30-day": "30-Day Discipline Journal", "90-day": "90-Day Discipline Journal" };

  const onBuyNotify = (product) => {
    setNotifyProduct(product);
    track(EVENTS.BUY_MODAL_OPENED, { product_id: product.id, from: `resources_${slug}` });
  };

  const onFreeDownload = () => {
    track(EVENTS.FREE_PDF_DOWNLOADED, { from: `resources_${slug}` });
  };

  return (
    <div className="min-h-screen bg-white text-[#333333]" data-testid={`resource-category-${slug}`}>
      <BrandHeader variant="cream" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-6 flex items-center gap-2 text-xs text-[#8A8A8A]" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-[#333333]">Wishing Wellness</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/resources" className="hover:text-[#333333]">Resources</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#333333] font-semibold">{title}</span>
      </div>

      <section className="pt-14 pb-16 md:pt-20 md:pb-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F6C453]/25 text-[#B8860B]">
            <Icon className="h-6 w-6" strokeWidth={1.75} />
          </span>
          <p className="mt-6 text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A]">{tagline}</p>
          <h1 className="mt-3 font-serif-display text-5xl sm:text-6xl font-medium tracking-tight text-[#333333] leading-[1.05]">{title}</h1>
          <p className="mt-6 text-lg md:text-xl text-[#4A4A4A] leading-relaxed">{intro}</p>
          {comingSoon?.badge && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#8FAF8A]/10 border border-[#8FAF8A]/30 text-[#6E8F6A] px-4 py-1.5 text-xs font-bold tracking-[0.2em] uppercase" data-testid="coming-soon-badge">
              <Sparkles className="h-3.5 w-3.5" /> {comingSoon.badge}
            </div>
          )}
        </div>
      </section>

      {items.length > 0 && (
        <section className="pb-16 md:pb-24 bg-white">
          <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-4">
            {items.map((it, i) => (
              <div
                key={it.title}
                className="bg-[#F8F6F2] border border-[#EDE9E1] rounded-3xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
                data-testid={`category-item-${i + 1}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-display font-bold text-xl text-[#333333]">{it.title}</h3>
                    {!it.available && !it.notify && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#8FAF8A] bg-[#8FAF8A]/10 rounded-full px-2.5 py-1">Coming Soon</span>
                    )}
                  </div>
                  <p className="mt-2 text-[#4A4A4A]">{it.body}</p>
                </div>
                {it.available && it.href ? (
                  <a
                    href={it.href}
                    target={it.external ? "_blank" : undefined}
                    rel={it.external ? "noreferrer" : undefined}
                    onClick={onFreeDownload}
                    className="inline-flex items-center gap-2 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 text-sm font-semibold whitespace-nowrap"
                    data-testid={`category-item-cta-${i + 1}`}
                  >
                    <Download className="h-4 w-4" /> {it.cta || "Get Free"}
                  </a>
                ) : it.notify ? (
                  <button
                    type="button"
                    onClick={() => onBuyNotify({ id: it.notify, title: titleMap[it.notify], price_label: priceLabelMap[it.notify] })}
                    className="inline-flex items-center gap-2 rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 text-sm font-semibold whitespace-nowrap"
                    data-testid={`category-item-cta-${i + 1}`}
                  >
                    Buy Now <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-2 rounded-full bg-white border border-[#EDE9E1] text-[#8A8A8A] h-12 px-6 text-sm font-semibold whitespace-nowrap">
                    Coming soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {comingSoon && (
        <>
          {/* Hero preview strip */}
          <section className="pb-16 md:pb-20 bg-white" data-testid="coming-soon-hero">
            <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-5 order-2 lg:order-1">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">A first look</p>
                <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333] leading-[1.1]">
                  What we&apos;re building for you.
                </h2>
                <p className="mt-5 text-[#4A4A4A] text-lg leading-relaxed">
                  {comingSoon.hero.pitch}
                </p>
                {comingSoon.ctaLink && (
                  <div className="mt-6">
                    <Button asChild className="rounded-full bg-[#333333] hover:bg-black text-white h-12 px-6 font-semibold" data-testid="coming-soon-secondary-cta">
                      <Link to={comingSoon.ctaLink}>{comingSoon.ctaLabel} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                    </Button>
                  </div>
                )}
              </div>
              <div className="lg:col-span-7 order-1 lg:order-2">
                <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(51,51,51,0.25)]">
                  <img src={comingSoon.hero.image} alt={`${title} preview`} className="w-full h-[300px] md:h-[440px] object-cover" />
                </div>
              </div>
            </div>
          </section>

          {/* Pillars: what to expect */}
          <section className="py-16 md:py-24 bg-[#F8F6F2]" data-testid="coming-soon-pillars">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <div className="max-w-2xl">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">What to expect</p>
                <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
                  Three things we&apos;re promising.
                </h2>
              </div>
              <div className="mt-10 grid md:grid-cols-3 gap-5 md:gap-6">
                {comingSoon.pillars.map(({ Icon: PIcon, title: pTitle, body }, i) => (
                  <motion.div
                    key={pTitle}
                    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
                    className="bg-white rounded-3xl p-7 border border-[#EDE9E1]"
                    data-testid={`coming-soon-pillar-${i + 1}`}
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F6C453]/25 text-[#B8860B]">
                      <PIcon className="h-5.5 w-5.5" strokeWidth={1.75} />
                    </span>
                    <h3 className="mt-5 font-display font-bold text-lg text-[#333333]">{pTitle}</h3>
                    <p className="mt-2 text-[#4A4A4A] text-sm leading-relaxed">{body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Teasers grid */}
          <section className="py-16 md:py-24 bg-white" data-testid="coming-soon-teasers">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <div className="max-w-2xl">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">A preview</p>
                <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
                  Some of what&apos;s coming.
                </h2>
                <p className="mt-4 text-[#4A4A4A]">A working sketch — not a promise of order. But this is the flavour.</p>
              </div>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-2 gap-5 md:gap-6">
                {comingSoon.teasers.map((t, i) => (
                  <motion.div
                    key={t.title}
                    initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: (i % 2) * 0.06, ease: EASE }}
                    className="bg-[#F8F6F2] rounded-3xl p-7 border border-[#EDE9E1] hover:shadow-lg transition-shadow"
                    data-testid={`coming-soon-teaser-${i + 1}`}
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-[#B8860B] bg-[#F6C453]/25 rounded-full inline-block px-2.5 py-1">
                      {t.label}
                    </div>
                    <h3 className="mt-4 font-display font-bold text-xl text-[#333333]">{t.title}</h3>
                    <p className="mt-2 text-[#4A4A4A] leading-relaxed">{t.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      <section className="py-16 md:py-24 bg-[#F8F6F2]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A] mb-4">Waitlist</p>
          <h2 className="font-serif-display text-3xl md:text-4xl font-medium tracking-tight text-[#333333]">
            Be the first to know.
          </h2>
          <p className="mt-4 text-[#4A4A4A]">
            {comingSoon?.waitlistNote || `We'll email you the moment new ${title.toLowerCase()} are ready.`}
          </p>
          <div className="mt-8 max-w-2xl mx-auto text-left">
            <EmailSignup source={comingSoon?.source || `resources_${slug}`} testIdPrefix="join" overline={null} headline={null} subtext={null} compact />
          </div>
        </div>
      </section>

      <BrandFooter />

      <BuyModal
        open={!!notifyProduct}
        onOpenChange={(v) => { if (!v) setNotifyProduct(null); }}
        product={notifyProduct}
      />
    </div>
  );
}
